import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, FileType2, Info, Loader2, Upload } from 'lucide-react';
import { Button } from '@/components/ui-kit/button';
import { useToast } from '@/hooks/use-toast';
import { useGetPreSignedUrlForUpload } from '@/lib/api/hooks/use-storage';
import { ModuleName } from '@/constant/modules.constants';
import { createTemplate } from '@/modules/templates/services/templates.service';
import graphqlClient from '@/graphql/graphql-client';
import { INSERT_TEMPLATE_DOCX_MUTATION } from '@/graphql/mutations/templates';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/state/store/auth';
import { slugify } from '@/lib/utils';

const projectKey = import.meta.env.VITE_X_BLOCKS_KEY || '';
const MAX_FILE_SIZE_MB = 8;

/** "Acme Mutual NDA (final).docx" → "Acme Mutual NDA (final)" */
function titleFromFilename(name: string): string {
  const base = name
    .replace(/\.docx$/i, '')
    .replace(/[_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return base.length > 0 ? base.slice(0, 200) : 'Untitled Word template';
}

/** Build a unique slug: first 20 chars of the slugified title + an 8-char uuid. */
async function resolveUniqueSlug(title: string): Promise<string> {
  const slug_title = slugify(title).slice(0, 20).replace(/-+$/, '');
  const uuid = crypto.randomUUID().replace(/-/g, '').slice(0, 8);
  return slug_title ? `${slug_title}-${uuid}` : uuid;
}

export default function NewDocxTemplateUploadStep() {
  const { t } = useTranslation();
  const { orgId: orgSlug } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { mutateAsync: getPreSignedUrl } = useGetPreSignedUrlForUpload();
  const queryClient = useQueryClient();
  const orgId = useAuthStore((state) => state.orgId);

  const [phase, setPhase] = useState<'idle' | 'uploading' | 'creating'>('idle');
  const [error, setError] = useState<string | null>(null);

  const busy = phase !== 'idle';

  const handleFile = useCallback(
    async (file: File) => {
      if (!file.name.toLowerCase().endsWith('.docx')) {
        setError('Only .docx files are supported.');
        return;
      }
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        setError(`File is too large. Maximum size is ${MAX_FILE_SIZE_MB} MB.`);
        return;
      }

      setError(null);
      setPhase('uploading');

      let templateId: string | null = null;

      try {
        // 1. Upload to Blocks storage
        const presigned = await getPreSignedUrl({
          name: file.name,
          projectKey,
          itemId: '',
          metaData: '',
          accessModifier: 'Public',
          configurationName: 'Default',
          parentDirectoryId: '',
          tags: '',
          moduleName: ModuleName.DefaultConstruct,
        });

        if (!presigned.isSuccess || !presigned.uploadUrl) {
          throw new Error('Failed to get upload URL from storage.');
        }

        await fetch(presigned.uploadUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type':
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'x-ms-blob-type': 'BlockBlob',
          },
        });

        const originalUrl = presigned.uploadUrl.split('?')[0];
        const originalKey = presigned.fileId || '';

        // 2. Create the Template row
        setPhase('creating');
        const title = titleFromFilename(file.name);
        const slug = await resolveUniqueSlug(title);

        const createResult = await createTemplate({
          Slug: slug as string,
          Title: title,
          Description: title,
          Kind: 'fixed_docx',
          Available: false,
          OrganizationId: orgId as string,
        });

        const data = createResult?.insertTemplates || createResult;
        templateId = data?.itemId as string | null;

        if (!templateId) {
          throw new Error('Template was not created — no itemId returned.');
        }

        // 3. Attach the uploaded file as TemplateDocx
        await graphqlClient.request(INSERT_TEMPLATE_DOCX_MUTATION, {
          TemplateId: templateId,
          OriginalUrl: originalUrl,
          OriginalKey: originalKey,
          OriginalName: file.name,
          SizeBytes: file.size,
          Status: 'uploaded',
        });

        toast({ variant: 'success', title: 'Document uploaded — ready for field tagging.' });
        // Navigate to templates list until the docx builder route is available.
        queryClient.invalidateQueries({ queryKey: ['ownTemplatesForOrg', orgId] });
        navigate(`/org/${orgSlug}/templates/${templateId}/docx`);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Something went wrong';
        setError(message);
        toast({ variant: 'destructive', title: message });

        if (templateId) {
          // Template row exists — send to templates list so the user can continue later.
          queryClient.invalidateQueries({ queryKey: ['ownTemplatesForOrg', orgId] });

          navigate(`/org/${orgSlug}/templates`);
          return;
        }
        setPhase('idle');
      }
    },
    [getPreSignedUrl, navigate, orgSlug, toast, queryClient, orgId]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const file = e.dataTransfer.files?.[0];
      if (file) handleFile(file);
    },
    [handleFile]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
      e.target.value = '';
    },
    [handleFile]
  );

  return (
    <div className=" w-full p-4 md:py-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button
          onClick={() => navigate(-1)}
          variant="ghost"
          size="icon"
          className="bg-brand-surface"
          disabled={busy}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-selise-blue/10 flex items-center justify-center shrink-0">
            <FileType2 className="h-6 w-6 text-selise-blue" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[hsl(var(--fg))] font-heading">
              {t('ORGTEMPLATES_DOCX_NEW_TITLE', { defaultValue: 'New Word template' })}
            </h1>
            <p className="text-sm text-globe-grey">
              {t('ORGTEMPLATES_DOCX_NEW_SUBTITLE', {
                defaultValue:
                  'Start with your document — upload it, then mark the parts that change',
              })}
            </p>
          </div>
        </div>
      </div>
      <div className="mx-auto max-w-3xl w-full">
        {error && (
          <div className="mb-4 p-3 rounded-md bg-crimson/10 text-crimson text-sm">{error}</div>
        )}

        {/* Upload zone / progress */}
        {busy ? (
          <div className="border-2 border-selise-blue/30 rounded-lg p-10 bg-selise-blue/5 flex flex-col items-center gap-3">
            <Loader2 className="w-10 h-10 text-selise-blue animate-spin" />
            <p className="text-sm text-muted-foreground">
              {phase === 'uploading'
                ? t('ORGTEMPLATES_DOCX_NEW_UPLOADING', { defaultValue: 'Uploading document…' })
                : t('ORGTEMPLATES_DOCX_NEW_CREATING', {
                    defaultValue: 'Setting up your template…',
                  })}
            </p>
          </div>
        ) : (
          <div
            className="flex flex-col items-center justify-center gap-4 rounded-lg border-2 border-dashed border-border p-8 transition-colors hover:border-selise-blue/50 hover:bg-selise-blue/5 cursor-pointer"
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            onClick={() => document.getElementById('docx-file-input')?.click()}
          >
            <Upload className="h-12 w-12 text-selise-blue" />
            <div className="text-center">
              <p className="font-medium text-[hsl(var(--fg))]">
                {t('ORGTEMPLATES_DOCX_NEW_DROPZONE_LABEL', {
                  defaultValue: 'Drop your Word document here or click to browse',
                })}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {t('ORGTEMPLATES_DOCX_NEW_DROPZONE_HINT', {
                  defaultValue: `Word .docx (max ${MAX_FILE_SIZE_MB} MB)`,
                })}
              </p>
            </div>
            <input
              id="docx-file-input"
              type="file"
              accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              className="hidden"
              onChange={handleInputChange}
            />
          </div>
        )}

        {/* Guidance */}
        <p className="mt-4 flex items-start gap-1.5 text-sm text-muted-foreground">
          <Info className="h-4 w-4 mt-0.5 shrink-0 text-selise-blue" />
          <span>
            {t('ORGTEMPLATES_DOCX_NEW_HINT', {
              defaultValue:
                'Upload a completed example — with real names, dates and amounts filled in — so you can point to the parts that change. Your formatting is preserved exactly.',
            })}
          </span>
        </p>

        <div className="mt-8">
          <Button
            variant="outline"
            disabled={busy}
            onClick={() => navigate(`/org/${orgSlug}/templates`)}
          >
            {t('ORGTEMPLATES_DOCX_NEW_CANCEL', { defaultValue: 'Cancel' })}
          </Button>
        </div>
      </div>
    </div>
  );
}
